/**
 * Error handling utilities for consistent error messages across the application
 */

export interface ApiError {
  response?: {
    data?: {
      detail?: string | { message?: string };
    };
    status?: number;
  };
  message?: string;
}

/**
 * Extract user-friendly error message from API error response
 * @param error - Error object from API call
 * @param fallbackMessage - Default message if specific error cannot be extracted
 * @returns User-friendly error message
 */
export const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const apiError = error as ApiError;

  // Check for response data detail
  if (apiError.response?.data?.detail) {
    const detail = apiError.response.data.detail;
    
    // Handle string detail
    if (typeof detail === 'string') {
      return detail;
    }
    
    // Handle object detail with message
    if (typeof detail === 'object' && detail.message) {
      return detail.message;
    }
  }

  // Check for error message
  if (apiError.message) {
    return apiError.message;
  }

  // Return fallback
  return fallbackMessage;
};

/**
 * Get HTTP status code from error
 * @param error - Error object from API call
 * @returns HTTP status code or null
 */
export const getErrorStatus = (error: unknown): number | null => {
  const apiError = error as ApiError;
  return apiError.response?.status || null;
};

/**
 * Check if error is a specific HTTP status
 * @param error - Error object from API call
 * @param status - HTTP status code to check
 * @returns True if error matches status
 */
export const isErrorStatus = (error: unknown, status: number): boolean => {
  return getErrorStatus(error) === status;
};

/**
 * Common error messages for invoice operations
 */
export const INVOICE_ERROR_MESSAGES = {
  LOAD_FAILED: 'Failed to load invoice details. Please try again.',
  NOT_FOUND: 'Invoice not found. It may have been deleted.',
  UPDATE_FAILED: 'Failed to update invoice. Please check your input and try again.',
  PDF_GENERATION_FAILED: 'Failed to generate PDF. Please try again later.',
  PAYMENT_LINK_FAILED: 'Failed to generate payment link. Please try again.',
  SEND_FAILED: 'Failed to send invoice. Please check customer contact details.',
  PERMISSION_DENIED: 'You do not have permission to perform this action.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
};
